import { useCallback, useMemo, type Dispatch, type SetStateAction } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getFullNameM } from '@shared/helpers/index';
import { useWindowWidth } from '@shared/hooks/index';
import { useCoachesInfiniteQuery } from '@src/api/api-hooks/coaches/useCoachesInfiniteQuery.ts';

export type CoachesListHeadPT = {
  filterState: [boolean, Dispatch<SetStateAction<boolean>>];
  searchingState: [boolean, Dispatch<SetStateAction<boolean>>];
  resetAllParams: () => void;
  searchByName: string;
  setSearchByName: (value: string) => void;
};

export const useCoachesListHead = ({
  filterState,
  searchingState,
  setSearchByName,
  searchByName,
}: Omit<CoachesListHeadPT, 'resetAllParams'>) => {
  const [searchParams] = useSearchParams();
  const { data } = useCoachesInfiniteQuery({
    search: searchParams.get('search') ?? undefined,
    tags: searchParams.get('tags') ?? undefined,
    city: searchParams.get('city') ?? undefined,
    consultations_type: searchParams.get('city') ?? undefined,
    partitions: searchParams.get('partitions') ?? undefined,
  });

  const specialistsNames = useMemo(() => {
    return (
      data?.results?.map((coach) =>
        getFullNameM(coach.first_name, coach.middle_name, coach.last_name)
      ) || []
    );
  }, [data]);

  const [isFilterOpen, setIsFilterOpen] = filterState;
  const [isSearching, setIsSearching] = searchingState;

  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 640;
  const isSearchingMobile = isSearching && isMobile;
  const isFilterMobile = isFilterOpen && isMobile;

  const changeFilterState = useCallback(() => setIsFilterOpen((f) => !f), [setIsFilterOpen]);
  const doIsSearchingOff = useCallback(() => setIsSearching(false), [setIsSearching]);
  const doIsSearchingOn = useCallback(() => setIsSearching(true), [setIsSearching]);

  const isClearFilterButtonDisabled =
    searchParams.size === 0 || (searchParams.has('searchByName') && searchParams.size === 1);

  return {
    searchByName,
    setSearchByName,
    specialistsNames,
    isClearFilterButtonDisabled,
    isSearchingMobile,
    isFilterMobile,
    changeFilterState,
    doIsSearchingOff,
    doIsSearchingOn,
    isSearching,
    isFilterOpen,
    setIsSearching,
    searchParams,
  };
};
